import { useSelector, useDispatch } from 'react-redux';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { palette } from '@m12s/component-library';
import _capitalize from 'lodash/capitalize';
import debounce from 'lodash/debounce';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import queryString from 'query-string';
import styled from 'styled-components';
import { farSyncAlt } from 'lib/icons';

import { SearchInput } from 'components/SearchInput';
import { BarcodeFieldChips } from 'components/BarcodeFieldChips';

import { actionPatchSelectWorkOrderOpOptions } from 'lib/actions';
import { parseSearchFields } from 'lib/utils/workOrderOperationSearch';
import { getProductionScheduleEnabled } from 'lib/selectors/getProductionScheduleEnabled';
import SwitchToggle from 'components/SwitchToggle';
import { Text } from 'components/Text';
import { getSelectWorkOrderOpOptions } from 'lib/selectors/getSelectWorkOrderOp';
import { getMachineSchedule } from 'lib/selectors/getMachineSchedule';
import { getEnabledBarcodeFields } from 'lib/selectors/getBarcodeFieldConfig';
import { useJobDisplayName } from 'lib/hooks/useJobDisplayName';

import { SpinningIcon } from './styled';
import { searchOnProductionScheduleOperations } from './helpers';
import useRequestUnscheduledWorkOrderOps from './hooks/useRequestUnscheduledWorkOrderOps';

const SwitchToggleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 1rem;
  margin-right: 1.5rem;
`;

const SwitchToggleText = styled(Text)`
  font-size: 0.875rem;
  color: ${palette.Grey700};
  font-weight: 500;
`;

const useSelectOperationFlow = ({
  selectedField,
  onFieldSelect,
  onFieldChange,
  onFieldDelete,
  machineScheduleLoading = false,
} = {}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const queryParams = queryString.parse(history.location.search);
  const productionScheduleEnabled = useSelector(getProductionScheduleEnabled);

  const {
    onlyMachineChecked,
    isSearchMode,
    search,
    fromBarcodeScan,
  } = useSelector(getSelectWorkOrderOpOptions);
  const { getOrderOpDisplayName } = useJobDisplayName();
  const enabledBarcodeFields = useSelector(getEnabledBarcodeFields);

  const [
    requestMachineWorkOrderOps,
    {
      operations: machineScheduledWorkOrderOps,
      loading: requestMachineWorkOrderOpsLoading,
      error: requestMachineWorkOrderOpsError,
      resultSearch: machineWorkOrderOpsResultSearch,
    },
  ] = useRequestUnscheduledWorkOrderOps({
    onlyAssignedToThisMachine: true,
    skip: onlyMachineChecked && !isSearchMode,
  });

  const [
    requestNotMachineWorkOrderOps,
    {
      operations: notMachineScheduledWorkOrderOps,
      loading: requestNotMachineWorkOrderOpsLoading,
      error: requestNotMachineWorkOrderOpsError,
    },
  ] = useRequestUnscheduledWorkOrderOps({
    onlyNotAssignedToThisMachine: true,
    skip: onlyMachineChecked || !isSearchMode,
  });

  const requestWorkOrderOps = useCallback(
    (opts) => {
      requestMachineWorkOrderOps(opts);
      requestNotMachineWorkOrderOps(opts);
    },
    [requestMachineWorkOrderOps, requestNotMachineWorkOrderOps]
  );

  const setOnlyMachineChecked = useCallback(
    (value) => {
      dispatch(
        actionPatchSelectWorkOrderOpOptions({
          onlyMachineChecked: value,
        })
      );
    },
    [dispatch]
  );

  const setOnlyAssignedChecked = useCallback(
    (value) => {
      dispatch(
        actionPatchSelectWorkOrderOpOptions({
          onlyAssignedChecked: value,
        })
      );
    },
    [dispatch]
  );

  const setSearch = useCallback(
    (value) => {
      dispatch(
        actionPatchSelectWorkOrderOpOptions({
          search: value,
        })
      );
    },
    [dispatch]
  );

  const [machineScheduleInit, setMachineScheduleInit] = useState(false);
  const machineSchedule = useSelector(getMachineSchedule);

  const excludeOperations = useMemo(() => {
    return queryParams?.excludeWorkOrderOperationRefs
      ? queryParams.excludeWorkOrderOperationRefs.split(',').map((ref) => {
          return ref;
        })
      : [];
  }, [queryParams.excludeWorkOrderOperationRefs]);

  useEffect(() => {
    if (!productionScheduleEnabled) {
      requestWorkOrderOps();
    } else if (machineSchedule?.length === 0) {
      if (!machineScheduleInit) {
        setMachineScheduleInit(true);
        setOnlyMachineChecked(false);
      }
    }
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [productionScheduleEnabled, machineSchedule]);

  useEffect(() => {}, [onlyMachineChecked, isSearchMode]);

  // The assigned query excludes scheduled jobs, so they count as assigned here.
  const assignedCheckPendingRef = useRef(true);
  useEffect(() => {
    if (!isSearchMode) {
      assignedCheckPendingRef.current = true;
      return;
    }
    if (!assignedCheckPendingRef.current) {
      return;
    }
    if (search) {
      assignedCheckPendingRef.current = false;
      return;
    }
    if (
      requestMachineWorkOrderOpsLoading ||
      machineScheduleLoading ||
      machineWorkOrderOpsResultSearch !== ''
    ) {
      return;
    }
    assignedCheckPendingRef.current = false;
    if (!machineScheduledWorkOrderOps.length && !machineSchedule?.length) {
      setOnlyAssignedChecked(false);
    }
  }, [
    isSearchMode,
    search,
    requestMachineWorkOrderOpsLoading,
    machineScheduleLoading,
    machineWorkOrderOpsResultSearch,
    machineScheduledWorkOrderOps.length,
    machineSchedule,
    setOnlyAssignedChecked,
  ]);

  // The picker clears fromBarcodeScan right away, except after an auto-selection,
  // so remember the scanned search only when the flag turns on.
  const scannedSearchRef = useRef(null);
  const prevFromBarcodeScanRef = useRef(false);
  useEffect(() => {
    if (fromBarcodeScan && !prevFromBarcodeScanRef.current) {
      scannedSearchRef.current = search;
    }
    prevFromBarcodeScanRef.current = fromBarcodeScan;
  }, [fromBarcodeScan, search]);
  useEffect(() => {
    const scannedSearch = scannedSearchRef.current;
    if (!scannedSearch) {
      return;
    }
    if (!isSearchMode || !onlyMachineChecked || search !== scannedSearch) {
      scannedSearchRef.current = null;
      return;
    }
    if (
      requestMachineWorkOrderOpsLoading ||
      machineScheduleLoading ||
      machineWorkOrderOpsResultSearch !== scannedSearch
    ) {
      return;
    }
    scannedSearchRef.current = null;
    const scheduledMatches = searchOnProductionScheduleOperations(
      scannedSearch,
      machineSchedule || [],
      getOrderOpDisplayName
    );
    if (!machineScheduledWorkOrderOps.length && !scheduledMatches?.length) {
      setOnlyAssignedChecked(false);
    }
  }, [
    isSearchMode,
    onlyMachineChecked,
    search,
    requestMachineWorkOrderOpsLoading,
    machineScheduleLoading,
    machineWorkOrderOpsResultSearch,
    machineScheduledWorkOrderOps.length,
    machineSchedule,
    getOrderOpDisplayName,
    setOnlyAssignedChecked,
  ]);

  // The request fns close over `search`, so their identity changes on every keystroke. Keep
  // refs to the latest ones so the debounced wrappers below can stay stable across renders.
  const requestMachineRef = useRef(requestMachineWorkOrderOps);
  const requestNotMachineRef = useRef(requestNotMachineWorkOrderOps);
  requestMachineRef.current = requestMachineWorkOrderOps;
  requestNotMachineRef.current = requestNotMachineWorkOrderOps;

  // Create the debounced fns ONCE. Previously `debounce(...)` ran in the render body, so a
  // fresh timer was created every render and it never actually debounced — a query fired on
  // nearly every keystroke, flashing the result groups in and out. Stable wrappers debounce
  // across keystrokes and call whatever the latest request fn is.
  const debouncedRequestMachineWorkOrderOps = useMemo(() => {
    return debounce(() => {
      return requestMachineRef.current();
    }, 850);
  }, []);
  const debouncedRequestNotMachineWorkOrderOps = useMemo(() => {
    return debounce(() => {
      return requestNotMachineRef.current();
    }, 850);
  }, []);

  useEffect(() => {
    if (isSearchMode && search?.length >= 0) {
      debouncedRequestMachineWorkOrderOps();
      debouncedRequestNotMachineWorkOrderOps();
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [search]);

  // Cancel trailing debounce timers on unmount so closing the picker right after a
  // keystroke doesn't fire a request into an unmounted hook.
  useEffect(() => {
    return () => {
      debouncedRequestMachineWorkOrderOps.cancel();
      debouncedRequestNotMachineWorkOrderOps.cancel();
    };
  }, [
    debouncedRequestMachineWorkOrderOps,
    debouncedRequestNotMachineWorkOrderOps,
  ]);

  const handleCheck = useCallback(() => {
    const newCheckedState = !onlyMachineChecked;
    if (isSearchMode) {
      setOnlyAssignedChecked(newCheckedState);
    } else {
      setOnlyMachineChecked(newCheckedState);
    }
  }, [
    onlyMachineChecked,
    isSearchMode,
    setOnlyAssignedChecked,
    setOnlyMachineChecked,
  ]);

  const pageLoading =
    requestMachineWorkOrderOpsLoading || requestNotMachineWorkOrderOpsLoading;
  const error =
    requestMachineWorkOrderOpsError || requestNotMachineWorkOrderOpsError;

  // Parse search string into fields for chip display
  // Only show chips if search has structured format (contains ': ')
  const hasStructuredFormat = search && search.includes(': ');
  const parsedFields = hasStructuredFormat ? parseSearchFields(search) : {};
  const hasFields = Object.keys(parsedFields).length > 0;

  const Components = {
    SearchInput: hasFields ? (
      <BarcodeFieldChips
        fields={parsedFields}
        selectedField={selectedField}
        onFieldSelect={onFieldSelect}
        onFieldChange={onFieldChange}
        onFieldDelete={onFieldDelete}
        fieldOrder={enabledBarcodeFields}
      />
    ) : (
      <SearchInput
        loading={pageLoading}
        iconPosition="start"
        inputStyle={{
          fontSize: '0.875rem',
          fontWeight: 400,
          color: palette.Grey600,
          height: '2.5rem',
        }}
        inputProps={{
          value: search,
          onChange: ({ target: { value } }) => {
            setSearch(value);
          },
          placeholder: _capitalize(
            t('Search by order, part, operation, or scan barcode')
          ),
        }}
      />
    ),
    SwitchToggle: pageLoading ? (
      <SpinningIcon icon={farSyncAlt} style={{ marginRight: '3rem' }} />
    ) : (
      <SwitchToggleContainer>
        <SwitchToggle
          onChange={handleCheck}
          isChecked={onlyMachineChecked}
          disabled={pageLoading}
        />
        <SwitchToggleText>
          {t(isSearchMode ? 'Assigned jobs only' : 'Scheduled jobs only')}
        </SwitchToggleText>
      </SwitchToggleContainer>
    ),
  };

  return {
    pageLoading,
    excludeOperations,
    machineScheduledWorkOrderOps,
    notMachineScheduledWorkOrderOps,
    error,
    Components,
  };
};

useSelectOperationFlow.propTypes = {
  setManualModalOpen: PropTypes.func,
  setNewRunModalOpen: PropTypes.func,
};

export default useSelectOperationFlow;
